import { wallOpeningOpening3 } from "building-common/openings/openingQuery";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type { RoofWallFaces } from "building-types/roofs/generatedRoofSystemTypes";
import { RoofWallFramingType } from "building-types/roofs/roofWallFramingTypes";
import { type RoofSide } from "building-types/settings/roofTypes";
import type { WallFaces } from "building-types/walls/wallFacesTypes";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { OPENING_TRANSFER_DISTANCE_TOLERANCE } from "doc/utils/roofTools";
import { aabb2Init, aabb2Union, intervalsOverlap } from "geom/aabb2";
import { aabb3Init, aabb3IsValid, aabb3Union } from "geom/aabb3";
import type { Face } from "geom/brep";
import { faceAABB3, faceOrNullCopy, faceOrNullMoveAlongNormal, faceOrNullReverse, tagFaces } from "geom/brep";
import type { ClipFaceContext } from "geom/bspClipping";
import { DIST_EPSILON } from "geom/core";
import { extrudeFaceAlongNormal, extrusionToBRep } from "geom/extrude";
import { planeIsCoplanar, planeMove, planeNormal, planeProjectPolyLine3 } from "geom/plane";
import {
  polyLine2AABB2,
  polyLine2EdgeTangent,
  polyLine2FromAABB2,
  polyLine2Length,
  polyLine2Move,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3AABB3, polyLine3OnPlane } from "geom/polyLine3";
import { vec2Dot } from "geom/vec2";
import { roofWallFacesNew } from "../roofs";
import { clipWallFaces, performWallCut3 } from "../walls/wallCut";

export function rebuildRoofWallFaces(roofWallFaces: RoofWallFaces) {
  const { edge, leftFaces, thickness, isGapFillingWall } = roofWallFaces;

  const backFaces: Face[] = [];
  const topFaces: Face[] = [];
  const bottomFaces: Face[] = [];
  const startFaces: Face[] = [];
  const endFaces: Face[] = [];

  const [dirX, dirY] = edge ? polyLine2EdgeTangent(edge, 0) : [1, 0];

  const frontFaces = leftFaces;
  for (const frontFace of frontFaces) {
    const extrusion = extrudeFaceAlongNormal(frontFace, -thickness);
    const [, backFace, ...sideFaces] = extrusionToBRep(extrusion).faces;

    for (const sideFace of sideFaces) {
      const [nX, nY, nZ] = planeNormal(sideFace.plane);

      if (nZ > DIST_EPSILON) {
        topFaces.push(sideFace);
      } else if (nZ < DIST_EPSILON) {
        bottomFaces.push(sideFace);
      } else {
        const dot = vec2Dot(nX, nY, dirX, dirY);
        if (dot < 0) {
          startFaces.push(sideFace);
        } else {
          endFaces.push(sideFace);
        }
      }
    }

    backFaces.push(backFace);
  }

  const tag = { type: "OPERATION", operation: "REBUILT_ROOF_WALL_FACE" };

  roofWallFaces.rightFaces = tagFaces(backFaces, tag);
  roofWallFaces.leftHoleFaces = [];
  roofWallFaces.rightHoleFaces = [];
  roofWallFaces.startFaces = isGapFillingWall ? [] : tagFaces(startFaces, tag);
  roofWallFaces.endFaces = isGapFillingWall ? [] : tagFaces(endFaces, tag);
  roofWallFaces.topFaces = isGapFillingWall ? [] : tagFaces(topFaces, tag);
  roofWallFaces.bottomFaces = isGapFillingWall ? [] : tagFaces(bottomFaces, tag);
  roofWallFaces.leftExteriorFaces = [];
  roofWallFaces.rightExteriorFaces = [];
}

export function clipRoofWallFaces(roofWallFaces: RoofWallFaces, options: ClipFaceContext) {
  clipWallFaces(roofWallFaces, options, true);
  rebuildRoofWallFaces(roofWallFaces);
}

export function clipRoofWallFacesNoRebuild(roofWallFaces: RoofWallFaces, options: ClipFaceContext) {
  clipWallFaces(roofWallFaces, options);
}

export function roofWallFacesFromFrontFace(
  framingType: RoofWallFramingType | undefined,
  frontFace: Face | null,
  edge: PolyLine2 | null,
  extendedEdge: PolyLine2 | null,
  side: RoofSide,
  addFacadeTrim: boolean,
  thickness: number = 1,
  isGapFillingWall: boolean = false,
): RoofWallFaces {
  return roofWallFacesFromFrontFaces(
    framingType,
    frontFace,
    frontFace ? [frontFace] : [],
    edge,
    extendedEdge,
    side,
    addFacadeTrim,
    thickness,
    isGapFillingWall,
  );
}

function roofWallFacesFromFrontFaces(
  framingType: RoofWallFramingType | undefined,
  srcFace: Face | null,
  frontFaces: Face[],
  edge: PolyLine2 | null,
  extendedEdge: PolyLine2 | null,
  side: RoofSide,
  addFacadeTrim: boolean,
  thickness: number = 0,
  isGapFillingWall: boolean = false,
): RoofWallFaces {
  const backFaces: Face[] = [];
  const topFaces: Face[] = [];
  const bottomFaces: Face[] = [];
  const startFaces: Face[] = [];
  const endFaces: Face[] = [];

  const addWall = framingType !== RoofWallFramingType.NONE;

  if (addWall) {
    const [dirX, dirY] = extendedEdge ? polyLine2EdgeTangent(extendedEdge, 0) : [1, 0];
    const extendedEdgeLen = extendedEdge ? polyLine2Length(extendedEdge) : 0;

    for (const frontFace of frontFaces) {
      const extrusion = extrudeFaceAlongNormal(frontFace, -thickness);
      const [, ...sideFaces] = extrusionToBRep(extrusion).faces;
      const backFace = makeRoofWallBackFace(frontFace, thickness, extendedEdgeLen);

      for (const sideFace of sideFaces) {
        const [nX, nY, nZ] = planeNormal(sideFace.plane);

        if (nZ > DIST_EPSILON) {
          topFaces.push(sideFace);
        } else if (nZ < DIST_EPSILON) {
          bottomFaces.push(sideFace);
        } else {
          const dot = vec2Dot(nX, nY, dirX, dirY);
          if (dot < 0) {
            startFaces.push(sideFace);
          } else {
            endFaces.push(sideFace);
          }
        }
      }

      if (!!backFace) {
        backFaces.push(backFace);
      }
    }
  }

  return roofWallFacesNew(
    addWall ? frontFaces : [],
    backFaces,
    srcFace,
    edge,
    extendedEdge,
    side,
    addFacadeTrim,
    thickness,
    isGapFillingWall,
    topFaces,
    bottomFaces,
    startFaces,
    endFaces,
  );
}

interface MakeRoofWallCutsOptions {
  readonly roofWallCuts: WallOpening[];
  readonly wallFaces: WallFaces;
  readonly distanceTolerance?: number;
}

export function makeRoofWallCuts({
  roofWallCuts,
  wallFaces,
  distanceTolerance = OPENING_TRANSFER_DISTANCE_TOLERANCE,
}: MakeRoofWallCutsOptions) {
  const { leftFaces, rightFaces } = wallFaces;

  let aabb3 = aabb3Init();
  let aabb2Left = aabb2Init();
  let aabb2Right = aabb2Init();
  let aabb3Left = aabb3Init();
  let aabb3Right = aabb3Init();

  leftFaces.forEach((x) => {
    const aabb3Face = faceAABB3(x, 0);
    const aabb2Face = polyLine2AABB2(x.loops[0]);
    aabb2Left = aabb2Union(aabb2Left, aabb2Face);
    aabb3 = aabb3Union(aabb3, aabb3Face);
    aabb3Left = aabb3Union(aabb3Left, aabb3Face);
  });

  rightFaces.forEach((x) => {
    const aabb3Face = faceAABB3(x, 0);
    const aabb2Face = polyLine2AABB2(x.loops[0]);
    aabb2Right = aabb2Union(aabb2Right, aabb2Face);
    aabb3 = aabb3Union(aabb3, aabb3Face);
    aabb3Right = aabb3Union(aabb3Right, aabb3Face);
  });

  if (!aabb3IsValid(aabb3)) {
    return;
  }

  const isValidLeftSide = aabb3IsValid(aabb3Left);
  const isValidRightSide = aabb3IsValid(aabb3Right);

  const { minZ: wallBottom, maxZ: wallTop } = aabb3;

  // look for wall cuts
  for (const roofWallCut of roofWallCuts) {
    const { frontPlane, backPlane } = roofWallCut;
    const frontOutline: PolyLine3 | undefined = wallOpeningOpening3(roofWallCut, "FRONT");
    const backOutline: PolyLine3 | undefined = wallOpeningOpening3(roofWallCut, "BACK");

    assertIsNotNullOrUndefined(frontOutline);

    const { minZ: bottom, maxZ: top } = polyLine3AABB3(frontOutline, 0);

    // look for vertical overlap
    if (wallBottom > top || wallTop < bottom) {
      continue;
    }

    // look for coplanar faces
    let leftIsFront = false;
    let isValidCut = false;

    if (isValidLeftSide) {
      if (planeIsCoplanar(frontPlane, leftFaces[0].plane, distanceTolerance)) {
        leftIsFront = true;
        isValidCut = isValidRightSide;
      } else if (planeIsCoplanar(backPlane, leftFaces[0].plane, distanceTolerance)) {
        leftIsFront = false;
        isValidCut = isValidRightSide;
      }
    } else if (isValidRightSide) {
      if (planeIsCoplanar(frontPlane, rightFaces[0].plane, distanceTolerance)) {
        leftIsFront = false;
        isValidCut = isValidLeftSide;
      } else if (planeIsCoplanar(backPlane, rightFaces[0].plane, distanceTolerance)) {
        leftIsFront = true;
        isValidCut = isValidLeftSide;
      }
    }

    if (!isValidCut) {
      continue;
    }

    const aabb2 = leftIsFront ? aabb2Right : aabb2Left;
    const frontWallPlane = leftIsFront ? leftFaces[0].plane : rightFaces[0].plane;
    const backWallPlane = leftIsFront ? rightFaces[0].plane : leftFaces[0].plane;
    const wallLoop = polyLine2FromAABB2(aabb2);
    const wallAabb = polyLine2AABB2(wallLoop);

    const frontLoop = planeProjectPolyLine3(frontWallPlane, frontOutline);

    let leftLoop: PolyLine2;
    if (leftIsFront) {
      leftLoop = frontLoop;
    } else {
      assertIsNotNullOrUndefined(backOutline);
      const backLoop = planeProjectPolyLine3(backWallPlane, backOutline);
      leftLoop = backLoop;
    }
    const cutAabb = polyLine2AABB2(leftLoop);
    if (!intervalsOverlap(wallAabb.minX, wallAabb.maxX, cutAabb.minX, cutAabb.maxX)) {
      continue;
    }

    const leftPlane = leftIsFront ? frontWallPlane : backWallPlane;
    performWallCut3(wallFaces, leftPlane, polyLine3OnPlane(leftLoop, leftPlane));
  }
}

export function makeRoofWallBackFace(frontFace: Face, wallThickness: number, edgeLen: number): Face | null {
  const backFace = faceOrNullReverse(faceOrNullCopy(frontFace));

  if (!backFace) {
    return null;
  }
  faceOrNullMoveAlongNormal(backFace, wallThickness);

  planeMove(backFace.plane, -backFace.plane.uX * edgeLen, -backFace.plane.uY * edgeLen, -backFace.plane.uZ * edgeLen);
  backFace.loops.forEach((loop) => polyLine2Move(loop, edgeLen, 0));

  return backFace;
}
